import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./Detailing.css";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";

/* ── Animation variant ──────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: "easeOut" },
  }),
};

/* ── Static data ────────────────────────────────────────── */
const packages = [
  {
    icon: "✦",
    title: "Ceramic Coating",
    description:
      "Industry-leading nano-ceramic protection that bonds to your paintwork, delivering a mirror-like gloss and shielding against UV, contaminants, and minor scratches.",
    features: [
      "9H hardness ceramic layer",
      "Hydrophobic water-beading",
      "UV & oxidation protection",
      "3–5 year durability",
      "Full paint decontamination",
    ],
  },
  {
    icon: "◈",
    title: "Interior Deep Clean",
    description:
      "A thorough top-to-bottom interior refresh that removes embedded dirt, odours, and bacteria from every surface — leaving your cabin showroom fresh.",
    features: [
      "Steam cleaning & sanitisation",
      "Leather conditioning",
      "Carpet & upholstery extraction",
      "Dashboard & console detailing",
      "Odour elimination treatment",
    ],
  },
  {
    icon: "◉",
    title: "Paint Correction",
    description:
      "Multi-stage machine polishing to remove swirl marks, light scratches, water spots, and oxidation — restoring your paint to a flawless, factory-fresh finish.",
    features: [
      "Single & multi-stage polishing",
      "Swirl & scratch removal",
      "Paint thickness measurement",
      "Panel wipe & IPA decontamination",
      "Post-correction sealant",
    ],
  },
  {
    icon: "⬡",
    title: "Full Detail Package",
    description:
      "Our flagship all-inclusive service combining exterior paint correction, ceramic coating and a complete interior deep clean in one comprehensive visit.",
    features: [
      "Exterior wash & clay bar",
      "Paint correction (multi-stage)",
      "Ceramic coating application",
      "Full interior deep clean",
      "Wheel & tyre dressing",
    ],
  },
  {
    icon: "◇",
    title: "Express Detail",
    description:
      "A premium maintenance detail that keeps your vehicle looking its best between full services — perfect for busy schedules without compromising on quality.",
    features: [
      "Hand wash & dry",
      "Interior vacuum & wipe-down",
      "Glass clean inside & out",
      "Tyre shine application",
      "Quick sealant top-up",
    ],
  },
  {
    icon: "△",
    title: "Paint Protection Film",
    description:
      "Self-healing PPF applied to high-impact areas, providing an invisible armour against stone chips, road debris, and everyday abrasions.",
    features: [
      "Self-healing top coat",
      "High-impact zone coverage",
      "Anti-yellowing formula",
      "10-year manufacturer warranty",
      "Gloss or matte finish options",
    ],
  },
];

const compRows = [
  {
    label: "Exterior pH neutral wash & decontamination",
    refresh: true,
    restore: true,
    protect: true,
  },
  {
    label: "1-stage paint enhancement",
    note: "Removes light swirls & adds gloss",
    refresh: true,
    restore: true,
    protect: true,
  },
  {
    label: "2-stage paint correction",
    note: "Removes heavier defects & scratches",
    refresh: false,
    restore: true,
    protect: true,
  },
  {
    label: "Water spot removal",
    refresh: false,
    restore: true,
    protect: true,
  },
  {
    label: "Full glass clean",
    note: "Inside & out",
    refresh: true,
    restore: true,
    protect: true,
  },
  {
    label: "Wheel clean & apply tyre dressing",
    refresh: true,
    restore: true,
    protect: true,
  },
  {
    label: "Hydrophobic protective wax sealant",
    refresh: true,
    restore: true,
    protect: true,
  },
  {
    label: "Add ceramic coating (optional)",
    note: "Long-term paint protection",
    refresh: false,
    restore: false,
    protect: true,
  },
];

const IMAGE_MAP = {
  1: { file: "rav4-19.jpeg", name: "Multi Brand Services", tag: "Featured" },
  2: { file: "rav4-10.jpeg", name: "Oil Services", tag: "Maintenance" },
  3: { file: "rav4-29.jpeg", name: "Wipers & Glass", tag: "Exterior" },
  4: { file: "rav4-03.jpeg", name: "Warrant of Fitness", tag: "Inspection" },
  5: { file: "rav4-13.jpeg", name: "Fleet Services", tag: "Fleet" },
  6: { file: "rav4-16.jpeg", name: "Battery Services", tag: "Electrical" },
  7: { file: "rav4-36.jpeg", name: "Mechanical Services", tag: "Mechanical" },
  8: { file: "rav4-35.jpeg", name: "Car Detailing", tag: "Detail" },
  9: { file: "rav4-41.jpeg", name: "Fleet Detailing", tag: "Fleet" },
};

/* ── Sub-components ─────────────────────────────────────── */
const Check = ({ val }) =>
  val ? (
    <span className="det-check-yes">✓</span>
  ) : (
    <span className="det-check-no" />
  );

const GalleryTile = ({ svcId, isHero = false }) => {
  const cfg = IMAGE_MAP[svcId];
  const src = cfg ? `/assets/detailing/${cfg.file}` : null;

  return (
    <div className={`det-tile${isHero ? " det-gallery-hero-tile" : ""}`}>
      {src ? (
        <img src={src} alt={cfg?.name || "Service image"} loading="lazy" />
      ) : (
        <div className="det-tile__placeholder">◉</div>
      )}
    </div>
  );
};

const PkgCard = ({ pkg, i }) => (
  <motion.div
    className="det-pkg-card"
    custom={i}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.15 }}
    variants={fadeUp}
  >
    <div className="det-pkg-card__accent" />
    <div className="det-pkg-card__icon">{pkg.icon}</div>
    <div className="det-pkg-card__title chakra-petch-bold">{pkg.title}</div>
    <p className="det-pkg-card__desc font-mulish">{pkg.description}</p>
    <ul className="det-pkg-features">
      {pkg.features.map((f, j) => (
        <li key={j} className="font-mulish">
          <span className="det-pkg-features__dot" />
          {f}
        </li>
      ))}
    </ul>
  </motion.div>
);

const TestiCard = ({ r, i }) => (
  <motion.div
    className="det-testi-card"
    custom={i}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.15 }}
    variants={fadeUp}
  >
    <div className="det-testi-card__accent" />
    <div className="det-quote-icon">"</div>
    <div className="det-testi-stars">{"★".repeat(r.rating)}</div>
    <p className="det-testi-card__text font-mulish">{r.text}</p>
    <div className="det-testi-author">
      <div className="det-author-avatar chakra-petch-bold">
        {r.author_name.charAt(0)}
      </div>
      <div>
        <span className="det-author-name chakra-petch-semibold">
          {r.author_name}
        </span>
        <span className="det-author-time font-mulish">
          {r.relative_time_description}
        </span>
      </div>
    </div>
  </motion.div>
);

/* ══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
const DetailingPage = () => {
  const navigate = useNavigate();
  const BOOK_URL = "https://booking.auxosoftware.com/w+WbahyJ_ES6NxDj+gBwFA==";

  const [settings, setSettings] = useState([]);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  useEffect(() => {
    fetch("https://admin.ezerautomotive.co.nz/api/settings/")
      .then((r) => r.json())
      .then(setSettings)
      .catch(() => { });

    fetch("https://admin.ezerautomotive.co.nz/api/services/")
      .then((r) => r.json())
      .then(setServices)
      .catch(() => { })
      .finally(() => setServicesLoading(false));

    fetch("https://admin.ezerautomotive.co.nz/api/google-place-review/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ place_id: "ChIJL_F3dWcibW0RqLmRoKNIRIk" }),
    })
      .then((r) => r.json())
      .then((json) =>
        setReviews((json.result?.reviews ?? []).filter((r) => r.rating >= 4)),
      )
      .catch(() => { })
      .finally(() => setReviewsLoading(false));
  }, []);

  const svcIds = services.length
    ? services.slice(0, 9).map((s) => s.id)
    : [1, 2, 3, 4, 5, 6, 7, 8, 9];

  return (
    <div className="det-page">
      <Header />

      {/* ══ HERO ══════════════════════════════════════════ */}
      <section className="det-hero">
        <div className="det-hero__bg" />
        <div className="det-hero__grad" />

        <div className="det-hero__content">
          <div className="det-breadcrumb font-mulish">
            <a href="/">Home</a>
            <span className="det-breadcrumb__sep">/</span>
            <span className="det-breadcrumb__active">Detailing Excellence</span>
          </div>

          <motion.div
            className="det-hero__eyebrow chakra-petch-bold"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Premium Automotive Care
          </motion.div>

          <motion.h1
            className="det-hero__h1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            DETAILING
            <br />
            <span>EXCELLENCE</span>
          </motion.h1>

          <motion.p
            className="det-hero__p font-mulish"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.6 }}
          >
            Premium automotive detailing engineered to protect, restore, and
            transform your vehicle — because your car deserves the best.
          </motion.p>

          <motion.div
            className="det-hero__ctas"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.5 }}
          >
            <button
              className="det-btn--primary chakra-petch-bold"
              onClick={() => window.open(BOOK_URL, "_blank")}
            >
              Book Now
            </button>
            <button
              className="det-btn--secondary chakra-petch-bold"
              onClick={() =>
                document
                  .getElementById("packages")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              View Packages
            </button>
          </motion.div>
        </div>

        {/* stat strip */}
        <motion.div
          className="det-hero__stats"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          {[
            ["12+", "Years of Experience"],
            ["99%", "Customer Delight"],
            ["5000+", "Vehicles Serviced"],
            ["15+", "Expert Technicians"],
          ].map(([num, lbl]) => (
            <div key={lbl} className="det-hero__stat">
              <span className="det-hero__stat-num chakra-petch-bold">
                {num}
              </span>
              <span className="det-hero__stat-label font-mulish">{lbl}</span>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ══ PACKAGES ══════════════════════════════════════ */}
      <section id="packages" className="det-section det-section--dark">
        <motion.div
          className="det-label chakra-petch-semibold"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          Our Services
        </motion.div>
        <motion.h2
          className="det-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={1}
        >
          OUR <span>PACKAGES</span>
        </motion.h2>
        <motion.p
          className="det-sub font-mulish"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={2}
        >
          Comprehensive detailing packages crafted for every need — from routine
          maintenance to show-car perfection.
        </motion.p>

        <div className="det-pkg-grid">
          {packages.map((pkg, i) => (
            <PkgCard key={i} pkg={pkg} i={i} />
          ))}
        </div>
      </section>

      {/* ══ COMPARISON TABLE ══════════════════════════════ */}
      <section className="det-section det-section--mid">
        <motion.div
          className="det-label chakra-petch-semibold"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          Compare &amp; Choose
        </motion.div>
        <motion.h2
          className="det-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={1}
        >
          DETAILING <span>PACKAGES</span>
        </motion.h2>
        <motion.p
          className="det-sub font-mulish"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={2}
        >
          Transparent, no-surprise pricing. Final quotes based on vehicle size
          and condition.
        </motion.p>

        <motion.div
          className="det-comp-container"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={3}
        >
          <div className="det-comp-wrapper">
            <table className="det-comp-table">
              <thead>
                <tr>
                  <th className="det-comp-th-features">
                    <span className="det-comp-th-label chakra-petch-bold">
                      Features &amp; Inclusions
                    </span>
                  </th>
                  <th>
                    <span className="det-comp-tier-name chakra-petch-bold">
                      Refresh
                    </span>
                    <span className="det-comp-tier-price chakra-petch-bold">
                      From $400
                    </span>
                    <span className="det-comp-tier-time font-mulish">
                      4–6 hrs
                    </span>
                  </th>
                  <th className="det-col-hl">
                    <span className="det-popular-badge chakra-petch-bold">
                      Most Popular
                    </span>
                    <span className="det-comp-tier-name chakra-petch-bold">
                      Restore
                    </span>
                    <span className="det-comp-tier-price chakra-petch-bold">
                      From $550
                    </span>
                    <span className="det-comp-tier-time font-mulish">
                      6–8 hrs
                    </span>
                  </th>
                  <th>
                    <span className="det-comp-tier-name chakra-petch-bold">
                      Protect
                    </span>
                    <span className="det-comp-tier-price chakra-petch-bold">
                      From $850
                    </span>
                    <span className="det-comp-tier-time font-mulish">
                      1–2 days
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {compRows.map((row, i) => (
                  <tr key={i}>
                    <td className="det-comp-feature-cell font-mulish">
                      <span className="det-feat-label">{row.label}</span>
                      {row.note && (
                        <span className="det-feat-note font-mulish">
                          {row.note}
                        </span>
                      )}
                    </td>
                    <td>
                      <Check val={row.refresh} />
                    </td>
                    <td className="det-col-hl">
                      <Check val={row.restore} />
                    </td>
                    <td>
                      <Check val={row.protect} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="chakra-petch-semibold">Approx. Time</td>
                  {[
                    ["4–6 hrs", false],
                    ["6–8 hrs", true],
                    ["1–2 days", false],
                  ].map(([t, hl]) => (
                    <td key={t} className={hl ? "det-col-hl" : ""}>
                      <span className="det-foot-val chakra-petch-bold">{t}</span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="chakra-petch-semibold">Price (Sedan/Hatch)</td>
                  {[
                    ["From $400", false],
                    ["From $550", true],
                    ["From $850", false],
                  ].map(([p, hl]) => (
                    <td key={p} className={hl ? "det-col-hl" : ""}>
                      <span className="det-foot-val det-foot-val--red chakra-petch-bold">
                        {p}
                      </span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="chakra-petch-semibold">Ideal For</td>
                  {[
                    [
                      "Refreshing to a near-factory look, or preparing to sell your vehicle.",
                      false,
                    ],
                    [
                      "Customers wanting a longer-lasting gloss finish or restoring a used car.",
                      true,
                    ],
                    [
                      "Ultimate long-term paint protection with ceramic coating and full interior.",
                      false,
                    ],
                  ].map(([txt, hl]) => (
                    <td key={txt} className={hl ? "det-col-hl" : ""}>
                      <span className="det-foot-sub font-mulish">{txt}</span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td />
                  {[false, true, false].map((hl, idx) => (
                    <td
                      key={idx}
                      className={hl ? "det-col-hl" : ""}
                      style={{ paddingTop: 20 }}
                    >
                      <button
                        className="det-book-btn chakra-petch-bold"
                        onClick={() => window.open(BOOK_URL, "_blank")}
                      >
                        Book Now
                      </button>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="det-comp-card">
            <div className="det-comp-card__body">
              <div className="det-comp-card__prices">
                <div className="det-comp-card__item font-mulish">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fd2906"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="det-comp-card__icon"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>
                    SUV detailing packages starting from <strong>$575</strong>
                  </span>
                </div>
                <div className="det-comp-card__item font-mulish">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fd2906"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="det-comp-card__icon"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>
                    UTE detailing packages starting from <strong>$585</strong>
                  </span>
                </div>
              </div>

              <div className="det-comp-card__brace" aria-hidden="true">
                <svg
                  viewBox="0 0 20 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="det-comp-card__brace-svg"
                >
                  <path
                    d="M4 4C14 4 14 34 20 40C14 46 14 76 4 76"
                    stroke="#fd2906"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              <div className="det-comp-card__note font-mulish">
                <span>Prices vary for different packages &amp; vehicle conditions</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ══ GALLERY ═══════════════════════════════════════ */}
      <section className="det-section det-section--dark">
        <motion.div
          className="det-label chakra-petch-semibold"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          Our Work
        </motion.div>
        <motion.h2
          className="det-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={1}
        >
          OUR <span>RESULTS</span>
        </motion.h2>
        <motion.p
          className="det-sub font-mulish"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={2}
        >
          Real results from real vehicles. Every photo is from a job completed
          right here at Ezer Automotive.
        </motion.p>

        {servicesLoading ? (
          <p className="det-gallery-loading font-mulish">Loading gallery…</p>
        ) : (
          <>
            {/* Row 1: large hero + 2 stacked */}
            <div className="det-gallery-featured">
              <GalleryTile svcId={svcIds[0]} isHero />
              <GalleryTile svcId={svcIds[1]} />
              <GalleryTile svcId={svcIds[2]} />
            </div>

            {/* Row 2: 3 landscape */}
            <div className="det-gallery-mid">
              {svcIds.slice(3, 6).map((id) => (
                <GalleryTile key={id} svcId={id} />
              ))}
            </div>

            {/* Row 3: 3 portrait + red CTA tile */}
            <div className="det-gallery-bottom">
              {svcIds.slice(6, 9).map((id) => (
                <GalleryTile key={id} svcId={id} />
              ))}
              <div
                className="det-tile det-tile--cta"
                onClick={() => navigate("/services")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigate("/services")}
              >
                <span className="det-tile--cta__label chakra-petch-bold">
                  See All
                </span>
                <span className="det-tile--cta__num chakra-petch-bold">9+</span>
                <span className="det-tile--cta__label chakra-petch-bold">
                  Services
                </span>
              </div>
            </div>

            <div className="det-gallery-footer">
              <div className="det-gallery-divider" />
              <span className="det-gallery-count chakra-petch-bold">
                9 Services Available
              </span>
              <div className="det-gallery-divider" />
            </div>
          </>
        )}
      </section>

      {/* ══ TESTIMONIALS ══════════════════════════════════ */}
      <section className="det-section det-section--mid">
        <motion.div
          className="det-label chakra-petch-semibold"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          Customer Reviews
        </motion.div>
        <motion.h2
          className="det-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={1}
        >
          CLIENT <span>REVIEWS</span>
        </motion.h2>
        <motion.p
          className="det-sub font-mulish"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={2}
        >
          Don't take our word for it — here's what our customers say about their
          Ezer Automotive detailing experience.
        </motion.p>

        {reviewsLoading ? (
          <p
            className="font-mulish"
            style={{
              color: "#333",
              textAlign: "center",
              marginTop: "2rem",
              fontSize: 13,
              letterSpacing: 2,
            }}
          >
            Loading reviews…
          </p>
        ) : reviews.length === 0 ? (
          <p
            className="font-mulish"
            style={{
              color: "#333",
              textAlign: "center",
              marginTop: "2rem",
              fontSize: 13,
            }}
          >
            No reviews available at this time.
          </p>
        ) : (
          <div className="det-testi-grid">
            {reviews.map((r, i) => (
              <TestiCard key={i} r={r} i={i} />
            ))}
          </div>
        )}
      </section>

      {/* ══ CTA ═══════════════════════════════════════════ */}
      <section className="det-cta">
        <div>
          <h2 className="det-cta__h2 chakra-petch-bold">
            BOOK YOUR <span>DETAIL</span> TODAY
          </h2>
          <p className="det-cta__p font-mulish">
            Ready to give your vehicle the treatment it deserves? Book your
            appointment online or call us directly — our team is standing by.
          </p>
        </div>
        <div className="det-cta__right">
          <button
            className="det-cta-btn chakra-petch-bold"
            onClick={() => window.open(BOOK_URL, "_blank")}
          >
            Book Appointment
          </button>
          <div className="det-cta__phone font-mulish">
            Or call us: <span>{settings[0]?.contact_number1 || "+64 …"}</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DetailingPage;
